import { useEffect, useState } from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { IRemoveSharingLinksActionResultProps } from "./remove-sharing-links-action-result.props";
import { IActionResult } from "@interfaces/actions/actions-common.interfaces";
import { IRemoveSharingLinksResult } from "@interfaces/actions/sharing-link-actions.interfaces";
import ActionResultNoResultCard from "components/actions-common/action-result-no-result-card/action-result-no-result-card";
import ServiceMessagesCard from "components/common/service-messages-card/service-messages-card";
import SpecificLinkResultCard from "./specific-link-result-card";
import ItemLinksResultCard from "./item-links-result-card";
import WorkspaceLinksResultCard from "./workspace-links-result-card";
import { RemovalScope } from "lib/enums";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";

const layoutStyles = makeStyles({
    mainWrapper: {
        ...shorthands.margin('1em', 0, 0, 0)
    }
});

export default function RemoveSharingLinksActionResult(props: IRemoveSharingLinksActionResultProps) {
    const styles = layoutStyles();
    const { t } = useL10n<ILocaleStrings>();

    const [actionResult, setActionResult] = useState<IActionResult<IRemoveSharingLinksResult>>();

    useEffect(() => {
        if (props.getResult) {
            props.getResult(`/api/actions/remove-sharing-links/${props.requestId}`).then(
                (data: IActionResult<IRemoveSharingLinksResult>) => {
                    console.log(`RemoveSharingLinksActionResult -> useEffect -> data -> `, data);
                    setActionResult(data);
                },
                (error: any) => {
                    console.log(`RemoveSharingLinksActionResult -> useEffect -> error -> `, error);
                });
        }
    }, [props.getResult, props.requestId]);

    // No complex processing needed - just use the raw data

    const [showMessages, setShowMessages] = useState<boolean>(false);
    const [showErrors, setShowErrors] = useState<boolean>(false);

    const renderResultCard = () => {
        if (!actionResult?.result) {
            return null;
        }

        const { removalScope, removed } = actionResult.result;

        // No results case
        if (!removed || removed.length === 0) {
            return (
                <ActionResultNoResultCard
                    message={t('ppaRemoveSharingLinksActionNoResultsMessage')}
                    errorCount={actionResult.errorCount}
                    messages={actionResult.messages}
                    onMessagesClick={() => setShowMessages(true)}
                    onErrorsClick={() => setShowErrors(true)} 
                />
            );
        }

        const commonProps = {
            errorCount: actionResult.errorCount,
            messages: actionResult.messages,
            onMessagesClick: () => setShowMessages(true),
            onErrorsClick: () => setShowErrors(true)
        };

        switch (removalScope) {
            case RemovalScope.SpecificLink:
                // Scenario 1: Remove specific sharing link from item
                return (
                    <SpecificLinkResultCard
                        removedLink={removed[0]}
                        {...commonProps}
                    />
                );

            case RemovalScope.AllLinksFromItem:
                // Scenario 2: Remove all sharing links from specific item
                return (
                    <ItemLinksResultCard
                        removedLinks={removed}
                        {...commonProps}
                    />
                );

            case RemovalScope.AllLinksFromWorkspace:
                // Scenario 3: Remove all sharing links from workspace
                return (
                    <WorkspaceLinksResultCard
                        removedLinks={removed}
                        {...commonProps}
                    />
                );

            default:
                return (
                    <ActionResultNoResultCard
                        message={t('ppaRemoveSharingLinksActionNoResultsMessage')}
                        {...commonProps}
                    />
                );
        }
    };

    return (
        <div className={styles.mainWrapper}>
            {!showErrors && !showMessages && renderResultCard()}
            {actionResult && showMessages &&
                <ServiceMessagesCard
                    title={t('messages')}
                    messages={actionResult.messages}
                    onClose={() => setShowMessages(false)}
                />}
            {actionResult && showErrors &&
                <ServiceMessagesCard
                    title={t('errors')}
                    messages={actionResult.messages.filter(m => m.type === 3 || m.type === 7)}
                    onClose={() => setShowErrors(false)}
                />}
        </div>
    );
}